// Shared client-side validation for the enquiry forms (booking-corporate-enquiry, booking-general-enquiry).
// Same conventions as the booking wizard: each control carries `data-validate` rules and a
// `data-error-key`, with a matching `[data-field-error="{key}"]` message slot. Server-side
// validation (BookingEnquiryForm) stays authoritative - this only shows errors sooner.
(() => {
    "use strict";

    const MESSAGES = {
        first_name: 'Please enter your first name.',
        last_name: 'Please enter your last name.',
        email: 'Please enter an email address.',
        phone: 'Please enter a phone number.',
        organisation: 'Please enter your company or organisation name.',
        job_title: 'Please enter your job title.',
        organisation_type: 'Please select your type of organisation.',
        industry: 'Please select your industry.',
        industry_other: 'Please tell us your industry.',
        attendees: 'Please enter the number of people attending.',
        group_size: 'Please enter the group size as a number.',
    };

    function errorSlot(root, input) {
        const key = input.dataset.errorKey;
        return key ? root.querySelector(`[data-field-error="${key}"]`) : null;
    }

    function clear(root, input) {
        const slot = errorSlot(root, input);
        if (slot) slot.classList.add('hidden');

        input.removeAttribute('aria-invalid');
        if (input.classList.contains('border-red-600')) {
            input.classList.replace('border-red-600', 'border-black/15');
        }
    }

    function show(root, input, message) {
        const slot = errorSlot(root, input);
        if (slot) {
            slot.textContent = message;
            slot.classList.remove('hidden');
        }

        input.setAttribute('aria-invalid', 'true');
        if (input.classList.contains('border-black/15')) {
            input.classList.replace('border-black/15', 'border-red-600');
        }
    }

    function check(input) {
        const value = input.value.trim();
        const key   = input.dataset.errorKey;

        for (const rule of (input.dataset.validate || '').split(',').filter(Boolean)) {
            if (rule === 'required' && value === '') return MESSAGES[key] || 'This field is required.';
            if (rule === 'email' && value !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Please enter a valid email address.';
            // Renato's note: 8 to 20 digits; spaces, brackets, dots and dashes ignored, a leading + allowed.
            if (rule === 'phone' && value !== '' && !/^\+?\d{8,20}$/.test(value.replace(/[\s().-]/g, ''))) return 'Please enter a phone number of 8 to 20 digits.';
            if (rule.startsWith('integer-min:') && value !== '') {
                const n = Number(value);
                if (!Number.isInteger(n) || n < parseInt(rule.split(':')[1], 10)) return MESSAGES[key] || 'Please enter a whole number.';
            }
        }

        return null;
    }

    window.sjmBookingForms = {
        /**
         * Validates every visible, enabled control with `data-validate` inside `container`.
         * @returns {HTMLElement|null} The first invalid control, or null.
         */
        validate(root, container) {
            let firstInvalid = null;

            container.querySelectorAll('[data-validate]').forEach((input) => {
                if (input.disabled || input.closest('[hidden]')) return;

                clear(root, input);
                const message = check(input);

                if (message) {
                    show(root, input, message);
                    if (!firstInvalid) firstInvalid = input;
                }
            });

            return firstInvalid;
        },

        /** Clears a field's error as soon as the visitor edits it. */
        clearOnInput(root) {
            root.addEventListener('input', (e) => {
                if (e.target.matches('[data-validate]')) clear(root, e.target);
            });
            root.addEventListener('change', (e) => {
                if (e.target.matches('select[data-validate]')) clear(root, e.target);
            });
        },
    };
})();
